import React from 'react';
import { View, Text, StyleSheet } from 'react-native';

import {
  availableInstallmentPlanDataFragment,
  installmentPlanDataFragment,
} from 'apollo/queries/autogenerate/operations';
import typography from 'constants/typography';
import colors from 'shared/modules/common/constants/colors';
import { toFullWidth } from 'shared/modules/common/utils/charge-utils';
import { formatMonthDate, formatToCurrency } from 'shared/utils/japanese';
type Props = {
  selectedOption:
    | availableInstallmentPlanDataFragment
    | installmentPlanDataFragment;
  currentInstallmentIndex: number;
};

export default function InstallmentOverview({
  selectedOption,
  currentInstallmentIndex,
}: Props) {
  if (
    !selectedOption ||
    selectedOption.installments.length < 1 ||
    currentInstallmentIndex < 0
  ) {
    return null;
  }

  const installmentTerm = selectedOption.installments.length;

  return (
    <View style={styles.container}>
      <Text
        style={{
          ...typography.caption01,
          paddingLeft: 32,
        }}
      >
        {toFullWidth(currentInstallmentIndex)}回目/
        {toFullWidth(installmentTerm)}回あと払い
      </Text>
      <Stepper
        steps={selectedOption.installments}
        activeIndex={currentInstallmentIndex - 1}
      />
    </View>
  );
}

function Stepper({
  steps,
  activeIndex = 0,
}: {
  steps: Array<{
    __typename?: 'Installment';
    id: string;
    dueDate: any;
    amount: number;
  }>;
  activeIndex: number;
}) {
  return (
    <View style={styles.stepperContainer}>
      {steps.map((step, index) => (
        <Step
          key={index}
          data={step}
          active={index === activeIndex || index < activeIndex}
          trackActive={index < activeIndex}
          isLastElement={index === steps.length - 1}
        />
      ))}
    </View>
  );
}

const Step = ({
  data,
  active,
  trackActive,
  isLastElement,
}: {
  data: {
    __typename?: 'Installment';
    id: string;
    dueDate: any;
    amount: number;
  };
  active: boolean;
  trackActive: boolean;
  isLastElement: boolean;
}) => {
  return (
    <>
      <View style={styles.stepContainer}>
        <View
          style={[
            styles.startEndRows,
            {
              alignItems: 'flex-end',
            },
          ]}
        >
          <Text
            style={{
              ...typography.caption02,
              color: active ? colors.greyDark : colors.greyMedium,
            }}
          >
            {formatMonthDate(data.dueDate)}
          </Text>
        </View>

        <View style={styles.centerRow}>
          <View style={[styles.indicator, active && styles.indicatorActive]} />
        </View>

        <View style={[styles.startEndRows, { alignItems: 'flex-start' }]}>
          <Text
            style={{
              ...typography.caption01,
              color: active ? colors.greyDark : colors.greyMedium,
            }}
          >
            {formatToCurrency(data.amount)}
          </Text>
        </View>
      </View>
      {!isLastElement && (
        <View
          style={[styles.separator, trackActive && styles.separatorActive]}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  stepperContainer: {
    marginTop: 16,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepContainer: {
    flexDirection: 'row',
    width: '100%',
  },
  startEndRows: {
    flex: 1,
    justifyContent: 'center',
  },
  centerRow: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  indicator: {
    width: 10,
    height: 10,
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: colors.greyMedium,
    borderRadius: 8,
    marginHorizontal: 16,
  },
  indicatorActive: {
    borderColor: colors.pink,
    backgroundColor: colors.white,
    shadowColor: '#E5258C',
    shadowOpacity: 0.8,
    shadowRadius: 5,
  },
  separator: {
    height: 16,
    width: 1,
    borderWidth: 1,
    borderColor: colors.greyMedium,
  },
  separatorActive: {
    borderColor: colors.pink,
  },
});
